import React, { useContext, useEffect, useRef, useState } from 'react'
import { parse, parseISO, format, isValid } from 'date-fns'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import PaymentService from '../../../services/PaymentService'
import { UserContext } from '../../../contexts/UserContext'
import { ToastsContext } from '../../../contexts/ToastsContext'
import { useSettings } from '../../../hooks/useSettings'
import { getPrimaryClass, getTextClass } from '../../../utils/themeUtils'

/**
 * L'importo di una riga dello storico, nella sua valuta.
 *
 * Il server scrive `amount_cents` (centesimi) e `currency`; con Managed Payments
 * (Adaptive Pricing) la valuta e' quella del cliente, non per forza euro o
 * dollaro. Le righe vecchie con `amount` in unita' restano leggibili.
 */
export const formatPaymentAmount = (payment, lingua = 'en') => {
  const units =
    typeof payment.amount_cents === 'number'
      ? payment.amount_cents / 100
      : typeof payment.amount === 'number'
        ? payment.amount
        : null
  if (units === null) return '—'
  return new Intl.NumberFormat(lingua, {
    style: 'currency',
    currency: (payment.currency || 'eur').toUpperCase(),
  }).format(units)
}

/**
 * La data di una riga: ISO come la scrive il server, o il vecchio
 * `dd.MM.yyyy HH:mm:ss`. Illeggibile = trattino (prima mandava in errore la scheda).
 */
export const formatPaymentDate = (value) => {
  if (!value) return '—'
  let date = parseISO(value)
  if (!isValid(date)) date = parse(value, 'dd.MM.yyyy HH:mm:ss', new Date())
  return isValid(date) ? format(date, 'MMM d, yyyy') : '—'
}

const PaymentHistory = () => {
  const effectRan = useRef(false)

  const [loading, setLoading] = useState(true)

  const { user, setUser } = useContext(UserContext)
  const { setToasts } = useContext(ToastsContext)
  const { currentTheme, isDarkMode } = useSettings()
  const { t, i18n } = useTranslation(['payments', 'errors'])

  useEffect(() => {
    if (effectRan.current === false) {
      PaymentService.getPaymentHistory(user.email)
        .then((res) =>
          setUser((prev) => ({ ...prev, paymentHistory: res.data }))
        )
        .catch((e) => {
          console.error('Get payment history failed:', e?.response?.data)
          setToasts((prev) => [
            ...prev,
            +process.env.REACT_APP_PROD_MODE_ENABLED
              ? t('history.toast.retrieveError', { ns: 'payments' })
              : e.response
                ? t('history.toast.retrieveErrorDetail', {
                    ns: 'payments',
                    status: e.response.status,
                    statusText: e.response.statusText,
                  })
                : t('history.toast.retrieveErrorMessage', {
                    ns: 'payments',
                    message: e.message,
                  }),
          ])
        })
        .finally(() => setLoading(false))
      return () => {
        effectRan.current = true
      }
    }
  }, [])

  return loading ? (
    <div className="flex items-center justify-center h-full">
      <img
        src="/assets/images/loader.svg"
        alt={t('history.loading', { ns: 'payments' })}
      />
    </div>
  ) : user && user?.paymentHistory && user.paymentHistory.length ? (
    <div
      className={clsx(
        'h-full flex flex-col border-2 rounded-xl overflow-hidden',
        getPrimaryClass(currentTheme),
        'border-current'
      )}
    >
      <div
        className={clsx(
          'grid grid-cols-5 items-center justify-items-center font-bold text-sm py-3 px-3 border-b-2 border-current',
          {
            'text-white':
              isDarkMode ||
              currentTheme === 'dark' ||
              currentTheme === 'dark-monochrome' ||
              currentTheme === 'darker-monochrome',
            'text-mid-blue': currentTheme === 'light',
          }
        )}
      >
        <div>{t('history.table.date', { ns: 'payments' })}</div>
        <div>{t('history.table.method', { ns: 'payments' })}</div>
        <div>{t('history.table.status', { ns: 'payments' })}</div>
        <div>{t('history.table.credits', { ns: 'payments' })}</div>
        <div>{t('history.table.total', { ns: 'payments' })}</div>
      </div>
      <div className="flex-1 overflow-y-auto px-3">
        <ul className="flex flex-col gap-2 py-2">
          {user.paymentHistory.map((payment, index) => (
            <li
              key={payment.id}
              className={clsx(
                'grid grid-cols-5 items-center justify-items-center font-light text-sm',
                {
                  'pb-3 border-b-2 border-current':
                    index !== user.paymentHistory.length - 1,
                  'text-white':
                    isDarkMode ||
                    currentTheme === 'dark' ||
                    currentTheme === 'dark-monochrome' ||
                    currentTheme === 'darker-monochrome',
                  'text-mid-blue': currentTheme === 'light',
                }
              )}
            >
              <div>{formatPaymentDate(payment.date)}</div>
              <div>{payment.payment_method}</div>
              <div
                className={clsx('px-2 py-px rounded', {
                  'bg-green/70 text-white': payment.status === 'succeeded',
                })}
              >
                {payment.status}
              </div>
              <div>+{payment.credits}</div>
              <div>{formatPaymentAmount(payment, i18n.language)}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  ) : (
    <div
      className={clsx(
        'flex items-center justify-center h-full',
        getTextClass(currentTheme)
      )}
    >
      {t('history.noHistory', { ns: 'payments' })}
    </div>
  )
}

export default PaymentHistory
